import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Break the crash loop if an invalid session token is causing a server error
    if (typeof window !== 'undefined' && window.location) {
      const searchParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      
      const hasError = searchParams.get('error') === 'server_error' || hashParams.get('error') === 'server_error';
      
      if (hasError) {
        console.warn('⚠️ Server auth error detected. Clearing invalid tokens to prevent crash loop...');
        
        // Remove all local storage Supabase auth keys
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
            localStorage.removeItem(key);
          }
        });
        localStorage.removeItem('sb-test-auth-token');
        localStorage.removeItem('sb-stable-auth-token');
        
        // Strip error params from URL to prevent infinite reloads
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        
        // Force quick reload to cleanly restart client
        window.location.reload();
        return;
      }
    }

    const getSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const currentUser = session?.user ?? null;
      setUser(currentUser);
      
      if (currentUser) {
        setIsAdmin(currentUser.email === 'howelldanr@gmail.com');
        fetchProfile(currentUser.id);
      } else {
        setLoading(false);
      }
    };

    getSession();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId) => {
    try {
      // Own-profile fetch goes through an RPC (not a raw select) because
      // location_state/discord_id/full_name/current_activity are
      // column-privilege-restricted to the row's own owner -- see
      // migration 47_profile_column_privacy.sql.
      const { data, error } = await supabase
        .rpc('get_my_profile')
        .maybeSingle();

      if (error) {
        console.error('Error fetching profile:', error);
      }
      setProfile(data);
    } catch (err) {
      console.error("Profile fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id);
      
      if (error) throw error;
      
      // Refresh
      fetchProfile(user.id);
      return { error: null };
    } catch (error) {
      return { error };
    }
  };

  const switchEnvironment = (targetMode) => {
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('leaf_env_mode', targetMode);
      // Force clean page reload to load the new Supabase client URL
      window.location.reload();
    }
  };

  const value = {
    user,
    profile,
    isAdmin,
    updateProfile,
    switchEnvironment,
    refreshProfile: () => user && fetchProfile(user.id),
    signIn: (email, password) => supabase.auth.signInWithPassword({ email, password }),
    signUp: (email, password) => supabase.auth.signUp({ email, password }),
    signInWithSSO: () => supabase.auth.signInWithOAuth({
      provider: 'keycloak',
      options: {
        redirectTo: window.location.origin,
        scopes: 'openid'
      }
    }),
    signOut: () => {
      setUser(null);
      setProfile(null);
      setIsAdmin(false);
      return supabase.auth.signOut();
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};